/**
 * MessageComposer Component
 *
 * Rich-text message input (tiptap) with bold/italic formatting.
 * - Bold (Ctrl/Cmd+B) and Italic (Ctrl/Cmd+I) via inline toolbar
 * - Enter to send, Shift+Enter for newline
 * - Image attachments, GIF picker, voice memos
 * - Typing indicators, draft save/restore, dynamic height
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Smile, X, Bold, Italic, Underline as UnderlineIcon, FileText, Loader2, Play } from 'lucide-react';
import EmojiPickerButton from '@/components/shared/EmojiPickerButton';
import { uploadWithProgress, uploadVideoWithProgress } from '@/lib/uploadVideoWithProgress';
import UploadProgress from '@/components/shared/UploadProgress';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { Extension } from '@tiptap/core';
import { Button } from '@/components/ui/button';
import { clearDraft, saveDraft, getDraft } from '@/lib/firestore';
import { toast } from 'sonner';
import ImagePreview from './ImagePreview';
import VoiceMemoRecorder, { VoiceMemoButton } from './VoiceMemoRecorder';
import AttachmentMenu from '@/components/shared/AttachmentMenu';
import PollCreatorDialog from './PollCreatorDialog';
import { GifPickerDialog } from '../comms/components/GifPicker';
import { stripHtml } from './utils/htmlUtils';
import { IMAGE_ACCEPT, isImageFile } from '@/lib/imageFiles';

const MAX_IMAGES = 10;

export default function MessageComposer({
  conversationId,
  currentUser,
  onSendText,
  onSendImages,
  onSendVoice,
  onSendVideo,
  onSendPoll,
  onTyping,
  onStopTyping,
  onHeightChange,
  disabled = false,
  replyingTo = null,
  onCancelReply,
}) {
  const [text, setText] = useState('');
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedGif, setSelectedGif] = useState(null);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [showPollCreator, setShowPollCreator] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [imageUploadProgress, setImageUploadProgress] = useState(null);
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [fileUploadProgress, setFileUploadProgress] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null); // { url, thumbnailUrl, duration, width, height }
  const [videoUploadProgress, setVideoUploadProgress] = useState(null); // null = idle, 0-100 = percent
  const [voiceUploadProgress, setVoiceUploadProgress] = useState(0);   // 0 when not sending voice, 0-100 during voice upload
  const fileInputRef = useRef(null);
  const fileAttachRef = useRef(null);
  const videoInputRef = useRef(null);
  const composerRef = useRef(null);
  const draftTimeoutRef = useRef(null);
  const handleSendRef = useRef(null);

  // ResizeObserver for dynamic scroll padding
  useEffect(() => {
    if (!composerRef.current || !onHeightChange) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        onHeightChange(entry.contentRect.height);
      }
    });
    observer.observe(composerRef.current);
    return () => observer.disconnect();
  }, [onHeightChange, isRecordingVoice]);

  // Tiptap editor — bold + italic only, Enter to send
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        bulletList: false,
        orderedList: false,
        blockquote: false,
        codeBlock: false,
        code: false,
        horizontalRule: false,
        hardBreak: true,
      }),
      Underline.configure({ HTMLAttributes: {} }),
      // Enter sends, Shift+Enter inserts line break
      Extension.create({
        name: 'enterToSend',
        addKeyboardShortcuts() {
          return {
            Enter: () => {
              handleSendRef.current?.();
              return true;
            },
            'Shift-Enter': ({ editor: ed }) => {
              ed.commands.setHardBreak();
              return true;
            },
          };
        },
      }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'outline-none max-h-36 overflow-y-auto text-base sm:text-[15px] text-slate-800 dark:text-slate-100 leading-6',
        style: 'min-height:24px;white-space:pre-wrap;word-break:break-word;',
      },
    },
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      const isEmpty = ed.isEmpty;
      const val = isEmpty ? '' : html;
      setText(val);
      saveDraftDebounced(val);
      if (!isEmpty && onTyping) onTyping();
    },
  });

  // Keep handleSendRef in sync so the keyboard shortcut always calls the latest version
  useEffect(() => {
    handleSendRef.current = handleSend;
  });

  // Save draft (debounced)
  const saveDraftDebounced = useCallback((newText) => {
    if (!conversationId || !currentUser?.id) return;
    if (draftTimeoutRef.current) clearTimeout(draftTimeoutRef.current);
    draftTimeoutRef.current = setTimeout(async () => {
      try { await saveDraft(currentUser.id, conversationId, newText); }
      catch (err) { console.error('Error saving draft:', err); }
    }, 500);
  }, [conversationId, currentUser?.id]);

  // Load draft / reset on conversation change
  useEffect(() => {
    if (!conversationId || !currentUser?.id) return;

    const loadDraft = async () => {
      try {
        const draft = await getDraft(currentUser.id, conversationId);
        if (draft && editor) {
          editor.commands.setContent(draft);
          setText(draft);
        }
      } catch (err) {
        console.error('Error loading draft:', err);
      }
    };
    loadDraft();

    return () => {
      setText('');
      setSelectedImages([]);
      setSelectedGif(null);
      setSelectedVideo(null);
      if (editor) editor.commands.clearContent();
    };
  }, [conversationId, currentUser?.id, editor]);

  // File selection
  const addImageFiles = (files) => {
    const imageFiles = files.filter(isImageFile);
    if (imageFiles.length === 0) return;
    const MAX_FILE_SIZE = 7 * 1024 * 1024; // R15: server cap is 7 MB
    if (imageFiles.some(f => f.size > MAX_FILE_SIZE)) {
      toast.error('Image file size must be under 7MB.');
      return;
    }
    const remainingSlots = MAX_IMAGES - selectedImages.length;
    if (remainingSlots <= 0) {
      toast.error(`Maximum ${MAX_IMAGES} images allowed.`);
      return;
    }
    const filesToAdd = imageFiles.slice(0, remainingSlots);
    const newImages = filesToAdd.map(file => ({ file, preview: URL.createObjectURL(file) }));
    setSelectedImages(prev => [...prev, ...newImages]);
    // Clear video — can't send both in a single DM
    if (selectedVideo) setSelectedVideo(null);
  };

  const handleFileSelect = (e) => {
    addImageFiles(Array.from(e.target.files || []));
  };

  const handleRemoveImage = (index) => {
    setSelectedImages(prev => {
      const copy = [...prev];
      URL.revokeObjectURL(copy[index].preview);
      copy.splice(index, 1);
      return copy;
    });
  };

  const handleClearImages = () => {
    selectedImages.forEach(img => URL.revokeObjectURL(img.preview));
    setSelectedImages([]);
  };

  const uploadAttachmentFiles = async (files) => {
    if (files.length === 0) return;
    if (selectedFiles.length + files.length > 5) {
      toast.error('Maximum 5 files allowed.');
      return;
    }
    if (files.some(f => f.size > 60 * 1024 * 1024)) { // R15: server cap is 60 MB
      toast.error('File size must be under 60MB.');
      return;
    }
    setIsUploadingFiles(true);
    setFileUploadProgress(0);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));
      const data = await uploadWithProgress(
        `${API_URL}/api/comms/v3/upload/files?user_id=${encodeURIComponent(currentUser.id)}`,
        formData,
        (pct) => setFileUploadProgress(pct),
        0
      );
      setSelectedFiles(prev => [...prev, ...data.files]);
    } catch (err) {
      console.error('Error uploading files:', err);
      toast.error(err.message || 'File upload failed.');
    } finally {
      setIsUploadingFiles(false);
      setFileUploadProgress(null);
      if (fileAttachRef.current) fileAttachRef.current.value = '';
    }
  };

  const handleFileAttachSelect = (e) => {
    uploadAttachmentFiles(Array.from(e.target.files || []));
  };

  const handleGifSelect = (gif) => {
    setSelectedGif(gif);
    setShowGifPicker(false);
    if (selectedImages.length > 0) handleClearImages();
    requestAnimationFrame(() => editor?.commands.focus());
  };

  // Video selection and upload
  const processVideoFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('video/')) { toast.error('Please select a video file.'); return; }
    if (file.size > 400 * 1024 * 1024) { toast.error('Video must be under 400MB.'); return; }

    // Client-side duration check
    try {
      const duration = await new Promise((resolve, reject) => {
        const vid = document.createElement('video');
        vid.preload = 'metadata';
        vid.onloadedmetadata = () => { URL.revokeObjectURL(vid.src); resolve(vid.duration); };
        vid.onerror = () => { URL.revokeObjectURL(vid.src); reject(new Error('Cannot read video')); };
        vid.src = URL.createObjectURL(file);
      });
      if (duration > 240) {
        toast.error('Video must be under 4 minutes. This video is ' + Math.floor(duration / 60) + ':' + String(Math.floor(duration % 60)).padStart(2, '0') + '.');
        return;
      }
    } catch { /* server will enforce */ }

    setVideoUploadProgress(0);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const data = await uploadVideoWithProgress(
        `${API_URL}/api/comms/v3/messaging/upload?user_id=${encodeURIComponent(currentUser.id)}&media_type=video`,
        file,
        (pct) => setVideoUploadProgress(pct)
      );
      const vid = data.media[0];
      setSelectedVideo({ url: vid.url, thumbnailUrl: vid.thumbnail_url, duration: vid.duration, width: vid.width, height: vid.height });
      // Clear images — can't send both in a single DM
      if (selectedImages.length > 0) handleClearImages();
      toast.success('Video uploaded!');
    } catch (err) {
      console.error('Error uploading video:', err);
      toast.error(err.message || 'Video upload failed.');
    } finally {
      setVideoUploadProgress(null);
    }
  };

  const handleVideoSelect = (e) => {
    const file = e.target.files?.[0];
    if (videoInputRef.current) videoInputRef.current.value = '';
    processVideoFile(file);
  };

  // ---- DRAG & DROP ----
  // Drop any mix of files onto the composer: images, one video, and documents
  // are routed to the same attach flows as the Attach menu.
  const DOC_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv', 'mp3', 'm4a', 'wav', 'ogg', 'aac', 'flac'];
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounterRef = useRef(0);

  const dragHasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');

  const handleDragEnter = (e) => {
    if (!dragHasFiles(e)) return;
    e.preventDefault();
    dragCounterRef.current += 1;
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    if (!dragHasFiles(e)) return;
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDragOver(false);
    }
  };

  const handleDrop = (e) => {
    if (!dragHasFiles(e)) return;
    e.preventDefault();
    e.stopPropagation(); // keep the rich-text editor from handling the drop itself
    dragCounterRef.current = 0;
    setIsDragOver(false);
    routeIncomingFiles(Array.from(e.dataTransfer.files || []));
  };

  const handlePasteCapture = (e) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (files.length === 0) return; // plain text — let the editor handle it
    e.preventDefault();
    e.stopPropagation();
    routeIncomingFiles(files);
  };

  const routeIncomingFiles = (files) => {
    if (disabled || isSending || files.length === 0) return;

    const images = files.filter(isImageFile);
    const videos = files.filter(f => f.type.startsWith('video/'));
    const docs = [];
    const unsupported = [];
    files.forEach(f => {
      if (isImageFile(f) || f.type.startsWith('video/')) return;
      const ext = (f.name.split('.').pop() || '').toLowerCase();
      if (DOC_EXTENSIONS.includes(ext)) docs.push(f);
      else unsupported.push(f);
    });

    if (images.length > 0) addImageFiles(images);
    if (videos.length > 0) {
      if (videos.length > 1) toast.error('Only one video per message — attaching the first one.');
      if (selectedVideo || videoUploadProgress !== null) toast.error('A video is already attached.');
      else processVideoFile(videos[0]);
    }
    if (docs.length > 0) uploadAttachmentFiles(docs);
    if (unsupported.length > 0) {
      toast.error(`Unsupported file type: ${unsupported.map(f => f.name).join(', ')}`);
    }
  };

  // Upload images
  const uploadImages = async (images) => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const formData = new FormData();
    images.forEach(img => formData.append('files', img.file));
    setImageUploadProgress(0);
    try {
      const result = await uploadWithProgress(
        `${API_URL}/api/comms/v3/messaging/upload?user_id=${encodeURIComponent(currentUser.id)}&media_type=image`,
        formData,
        (pct) => setImageUploadProgress(pct),
        0
      );
      return result.media.map(item => ({ url: item.url, width: 0, height: 0 }));
    } finally {
      setImageUploadProgress(null);
    }
  };

  // ---- SEND ----
  // eslint-disable-next-line react-hooks/exhaustive-deps
  function handleSend() {
    const hasText = stripHtml(text).length > 0;
    const hasImages = selectedImages.length > 0;
    const hasGif = !!selectedGif;
    const hasFiles = selectedFiles.length > 0;
    const hasVideo = !!selectedVideo;
    if (!hasText && !hasImages && !hasGif && !hasFiles && !hasVideo) return;
    if (isSending || disabled) return;
    if (!conversationId || !currentUser?.id) return;

    setIsSending(true);

    if (draftTimeoutRef.current) { clearTimeout(draftTimeoutRef.current); draftTimeoutRef.current = null; }
    if (onStopTyping) onStopTyping();

    const imagesToSend = [...selectedImages];
    const gifToSend = selectedGif;
    const textToSend = text;
    const filesToSend = [...selectedFiles];
    const videoToSend = selectedVideo;

    // Clear editor immediately (optimistic)
    setText('');
    setSelectedImages([]);
    setSelectedGif(null);
    setSelectedFiles([]);
    setSelectedVideo(null);
    if (editor) editor.commands.clearContent();

    (async () => {
      try {
        // Build text with file attachments appended
        let finalText = textToSend;
        if (filesToSend.length > 0 && onSendText) {
          const fileHtml = filesToSend.map(f =>
            `<p data-file-attachment="true" data-file-url="${f.url}" data-file-name="${f.filename}" data-file-size="${f.size}" data-file-type="${f.type}"><a href="${f.url}" target="_blank" rel="noopener noreferrer">${f.filename}</a></p>`
          ).join('');
          finalText = (finalText || '') + fileHtml;
        }

        if (videoToSend) {
          // Send video message
          if (onSendVideo) await onSendVideo(videoToSend, (hasText || hasFiles) ? finalText : '');
          await clearDraft(currentUser.id, conversationId);
        } else if (gifToSend) {
          const gifImage = [{ url: gifToSend.url, width: 0, height: 0 }];
          if (onSendImages) await onSendImages(gifImage, (hasText || hasFiles) ? finalText : '');
          await clearDraft(currentUser.id, conversationId);
        } else if (imagesToSend.length > 0) {
          setIsUploading(true);
          const uploaded = await uploadImages(imagesToSend);
          setIsUploading(false);
          if (onSendImages) await onSendImages(uploaded, (hasText || hasFiles) ? finalText : '');
          await clearDraft(currentUser.id, conversationId);
        } else if (hasText || hasFiles) {
          if (onSendText) await onSendText(finalText);
          await clearDraft(currentUser.id, conversationId);
        }
        imagesToSend.forEach(img => URL.revokeObjectURL(img.preview));
      } catch (err) {
        console.error('Error preparing message:', err);
        setText(textToSend);
        setSelectedImages(imagesToSend);
        setSelectedGif(gifToSend);
        setSelectedFiles(filesToSend);
        setSelectedVideo(videoToSend);
        if (editor) editor.commands.setContent(textToSend);
      } finally {
        setIsSending(false);
        setIsUploading(false);
        requestAnimationFrame(() => editor?.commands.focus());
      }
    })();
  }

  // Voice recording
  const handleVoiceRecordingComplete = async (voiceMemoData) => {
    if (!conversationId || !currentUser?.id) return;
    setIsSending(true);
    setVoiceUploadProgress(0);
    // Keep the recorder visible during upload so the percentage indicator
    // (passed to VoiceMemoRecorder as uploadProgress prop) has somewhere to
    // render. Recorder is dismissed AFTER the upload completes below.
    if (onStopTyping) onStopTyping();

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const formData = new FormData();
      const isWebm = voiceMemoData.mimeType?.includes('webm');
      const fileName = `voice_memo_${Date.now()}.${isWebm ? 'webm' : 'm4a'}`;
      formData.append('files', voiceMemoData.blob, fileName);

      // Real XHR byte-accurate progress. 10% reserved for backend → Supabase write.
      const result = await uploadWithProgress(
        `${API_URL}/api/comms/v3/messaging/upload?user_id=${encodeURIComponent(currentUser.id)}&media_type=voice`,
        formData,
        setVoiceUploadProgress,
        10,
      );
      const uploadedMedia = result.media[0];
      // Dismiss recorder ONLY after a successful upload + before invoking the
      // onSendVoice callback — that way the user sees 100% briefly and then
      // the recorder disappears.
      setIsRecordingVoice(false);
      if (onSendVoice) {
        await onSendVoice({ url: uploadedMedia.url, duration: voiceMemoData.duration, mimeType: voiceMemoData.mimeType });
      }
    } catch (err) {
      console.error('Error sending voice memo:', err);
      toast.error(err?.message || 'Failed to send voice memo. Please try again.');
      // On failure, also dismiss recorder so user isn't stuck (toast informs them).
      setIsRecordingVoice(false);
    } finally {
      setIsSending(false);
      setVoiceUploadProgress(0);
    }
  };

  const showSendButton = stripHtml(text).length > 0 || selectedImages.length > 0 || !!selectedGif;

  // Voice recording view
  if (isRecordingVoice) {
    return (
      <div ref={composerRef} data-ui="composer" className="border-t border-slate-200 dark:border-white/10 bg-card p-3 md:relative md:bottom-auto md:left-auto md:right-auto md:z-auto fixed bottom-[calc(40px+env(safe-area-inset-bottom))] left-0 right-0 z-25">
        <VoiceMemoRecorder
          onRecordingComplete={handleVoiceRecordingComplete}
          onCancel={() => setIsRecordingVoice(false)}
          disabled={disabled || isSending}
          uploadProgress={voiceUploadProgress}
        />
      </div>
    );
  }

  return (
    <div
      ref={composerRef}
      data-ui="composer"
      className="relative border-t border-slate-200 dark:border-white/10 bg-card safe-area-bottom md:relative md:bottom-auto md:left-auto md:right-auto md:z-auto fixed bottom-[calc(40px+env(safe-area-inset-bottom))] left-0 right-0 z-25"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOverCapture={(e) => { if (dragHasFiles(e)) e.preventDefault(); }}
      onDropCapture={handleDrop}
      onPasteCapture={handlePasteCapture}
    >
      {/* Drag & drop overlay */}
      {isDragOver && (
        <div
          data-ui="drop-ov"
          className="absolute inset-0 z-50 flex items-center justify-center rounded-lg border-2 border-dashed border-blue-400 bg-blue-50/90 dark:bg-blue-950/80 pointer-events-none"
          data-testid="dm-drop-overlay"
        >
          <span className="text-sm font-medium text-blue-600 dark:text-blue-300">Drop files to attach</span>
        </div>
      )}
      {/* Reply preview bar */}
      {replyingTo && (
        <div data-ui="reply-strip" className="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-[#1e1f22] border-b border-slate-100 dark:border-white/10">
          <div data-ui="reply-quote" className="flex-1 min-w-0 border-l-2 border-blue-400 pl-2">
            <div data-ui="quote-name" className="text-xs font-semibold text-blue-500 dark:text-blue-400">{replyingTo.senderName || 'User'}</div>
            <div className="text-xs text-slate-400 dark:text-slate-500 truncate">{stripHtml(replyingTo.text || '') || 'Message'}</div>
          </div>
          <button
            onClick={onCancelReply}
            data-ui="ibtn-sm"
            className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-slate-200 text-slate-400 dark:text-slate-500 flex-shrink-0"
            data-testid="cancel-reply-btn"
          >
            <span className="text-sm">✕</span>
          </button>
        </div>
      )}

      {/* Image previews */}
      <ImagePreview
        images={selectedImages}
        onRemove={handleRemoveImage}
        onClear={handleClearImages}
      />

      {/* GIF preview */}
      {selectedGif && (
        <div data-ui="gif-prev" className="px-3 pt-2" data-testid="gif-preview">
          <div className="relative inline-block">
            <img
              src={selectedGif.preview || selectedGif.url}
              alt={selectedGif.title || 'GIF'}
              className="h-24 rounded-lg object-cover border border-slate-200 dark:border-white/10"
            />
            <button
              onClick={() => setSelectedGif(null)}
              className="absolute -top-1.5 -right-1.5 bg-slate-700 hover:bg-slate-900 text-white rounded-full p-0.5 shadow-sm transition-colors"
              data-testid="remove-gif-btn"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* File attachment previews */}
      {selectedFiles.length > 0 && (
        <div className="px-3 pt-2 flex flex-col gap-1.5">
          {selectedFiles.map((file, index) => (
            <div key={index} data-ui="cmp-file" className="flex items-center gap-2 px-2.5 py-1.5 bg-slate-50 dark:bg-[#1e1f22] rounded-lg border border-slate-200 dark:border-white/10">
              <FileText className="w-4 h-4 text-slate-400 dark:text-slate-500 flex-shrink-0" />
              <span className="text-xs text-slate-600 dark:text-slate-300 truncate flex-1">{file.filename}</span>
              <span className="text-xs text-slate-400 dark:text-slate-500 flex-shrink-0">{(file.size / 1024).toFixed(0)} KB</span>
              <button
                onClick={() => setSelectedFiles(prev => prev.filter((_, i) => i !== index))}
                className="text-slate-400 dark:text-slate-500 hover:text-red-500 p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="px-2 pt-2 pb-3 sm:p-3">
        {/* Top toolbar — attachment menu always visible; formatting buttons
            appear once the rich-text editor is mounted/focused. */}
        <div data-ui="cmp-tools" className="flex items-center gap-0.5 mb-1">
          <AttachmentMenu
            testIdPrefix="dm"
            size="sm"
            align="start"
            preserveFocus
            onClose={() => { if (editor) editor.commands.focus(); }}
            triggerClassName="h-7 w-7 p-1 rounded hover:bg-slate-100 dark:hover:bg-white/10"
            onImageClick={() => fileInputRef.current?.click()}
            onVideoClick={() => videoInputRef.current?.click()}
            onFileClick={() => fileAttachRef.current?.click()}
            showPoll={!!onSendPoll}
            onPollClick={() => setShowPollCreator(true)}
            pollDisabled={disabled || isSending}
            imageDisabled={disabled || isSending || selectedImages.length >= MAX_IMAGES}
            videoDisabled={disabled || isSending || videoUploadProgress !== null || !!selectedVideo}
            fileDisabled={disabled || isSending || isUploadingFiles || selectedFiles.length >= 5}
            uploadProgress={
              imageUploadProgress !== null ? imageUploadProgress
              : videoUploadProgress !== null ? videoUploadProgress
              : fileUploadProgress !== null ? fileUploadProgress
              : null
            }
            activeUploadLabel={
              videoUploadProgress !== null ? 'video'
              : imageUploadProgress !== null ? 'image'
              : fileUploadProgress !== null ? 'file'
              : ''
            }
          />
          {editor && (
            <>
              <button
                type="button"
                data-ui="fmt-btn"
                data-active={editor.isActive('bold') ? 'true' : undefined}
                onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBold().run(); }}
                className={`p-1 rounded transition-colors ${
                  editor.isActive('bold')
                    ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400'
                    : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/10'
                }`}
                title="Bold (Ctrl+B)"
                data-testid="format-bold-btn"
              >
                <Bold className="w-4 h-4" />
              </button>
              <button
                type="button"
                data-ui="fmt-btn"
                data-active={editor.isActive('italic') ? 'true' : undefined}
                onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleItalic().run(); }}
                className={`p-1 rounded transition-colors ${
                  editor.isActive('italic')
                    ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400'
                    : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/10'
                }`}
                title="Italic (Ctrl+I)"
                data-testid="format-italic-btn"
              >
                <Italic className="w-4 h-4" />
              </button>
              <button
                type="button"
                data-ui="fmt-btn"
                data-active={editor.isActive('underline') ? 'true' : undefined}
                onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleUnderline().run(); }}
                className={`p-1 rounded transition-colors ${
                  editor.isActive('underline')
                    ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400'
                    : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/10'
                }`}
                title="Underline (Ctrl+U)"
                data-testid="format-underline-btn"
              >
                <UnderlineIcon className="w-4 h-4" />
              </button>
            </>
          )}
        </div>

        <div className="flex items-end gap-1.5 sm:gap-2">
          {/* Hidden file inputs — triggered by the AttachmentMenu in the
              toolbar row above. Kept here so refs and onChange handlers
              are colocated with the rest of the composer's file logic. */}
          <input
            ref={fileInputRef}
            type="file"
            accept={IMAGE_ACCEPT}
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
          <input
            ref={fileAttachRef}
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.mp3,.m4a,.wav,.ogg,.aac,.flac"
            multiple
            onChange={handleFileAttachSelect}
            className="hidden"
          />
          <input
            ref={videoInputRef}
            type="file"
            accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm"
            onChange={handleVideoSelect}
            className="hidden"
          />

          {/* Rich text input */}
          <div className="flex-1 relative min-w-0">
            <div data-ui="composer-field" className="flex items-end bg-slate-100 dark:bg-[#3a3d41] rounded-2xl px-3 sm:px-4 py-2 focus-within:bg-slate-50 focus-within:ring-2 focus-within:ring-blue-500 transition-all">
              <EditorContent
                editor={editor}
                className="flex-1 min-w-0 [&_.tiptap]:outline-none [&_.tiptap]:max-h-36 [&_.tiptap]:overflow-y-auto [&_.tiptap_p]:m-0 [&_.tiptap]:min-h-[24px] placeholder:text-slate-400 dark:placeholder:text-slate-500"
                data-testid="message-input"
              />
              {/* Emoji button inside input */}
              <EmojiPickerButton
                title="Insert emoji"
                side="top"
                iconClassName="w-5 h-5"
                data-ui="ibtn-sm"
                className="text-slate-400 dark:text-slate-500 hover:text-yellow-500 h-6 w-6 ml-1 flex-shrink-0 inline-flex items-center justify-center transition-colors"
                onEmojiSelect={(emoji) => editor?.chain().focus().insertContent(emoji).run()}
              />
              {/* GIF button inside input */}
              <Button
                variant="ghost"
                size="icon"
                data-ui="ibtn-sm"
                className="text-slate-400 dark:text-slate-500 hover:text-yellow-500 h-6 w-6 ml-1 flex-shrink-0"
                disabled={disabled || isSending}
                onClick={() => setShowGifPicker(true)}
                title="Choose a GIF"
                data-testid="gif-picker-btn"
              >
                <span className="text-[9px] font-bold tracking-wide border-[1.5px] border-current rounded px-0.5 leading-tight">GIF</span>
              </Button>
            </div>
          </div>

          {/* Send / Voice button */}
          {showSendButton ? (
            <Button
              onClick={handleSend}
              onMouseDown={(e) => e.preventDefault()}
              disabled={disabled || isSending || (!stripHtml(text) && selectedImages.length === 0 && !selectedGif && !selectedVideo)}
              data-ui="send-btn"
              className="bg-blue-500 hover:bg-blue-600 text-white rounded-full h-10 w-10 p-0 flex-shrink-0 transition-all active:scale-95"
              title="Send message"
              data-testid="send-message-btn"
            >
              <Send className="w-5 h-5" />
            </Button>
          ) : (
            <VoiceMemoButton
              onClick={() => setIsRecordingVoice(true)}
              disabled={disabled || isSending}
            />
          )}
        </div>

        {isUploading && (
          <div data-ui="upl-line" className="flex items-center gap-2 text-xs text-blue-500 mt-1 ml-12 font-medium">
            <UploadProgress progress={imageUploadProgress ?? 0} size={14} className="text-blue-500" />
            <span>
              {imageUploadProgress !== null && imageUploadProgress >= 90 && imageUploadProgress < 100
                ? 'Processing images…'
                : 'Uploading images…'}
            </span>
          </div>
        )}
        {videoUploadProgress !== null && (
          <div data-ui="upl-line" className="flex items-center gap-2 text-xs text-blue-500 mt-1 ml-12 font-medium">
            <UploadProgress progress={videoUploadProgress} size={14} className="text-blue-500" />
            <span>
              {videoUploadProgress >= 90 && videoUploadProgress < 100
                ? 'Processing video…'
                : 'Uploading video…'}
            </span>
          </div>
        )}
        {selectedVideo && (
          <div className="mt-1.5 ml-12 relative inline-block">
            <div data-ui="vid-thumb" className="h-16 w-16 rounded-lg overflow-hidden border border-slate-200 dark:border-white/10 bg-black relative">
              {selectedVideo.thumbnailUrl ? (
                <img src={selectedVideo.thumbnailUrl} alt="Video" className="h-full w-full object-cover" />
              ) : (
                <div className="h-full w-full flex items-center justify-center bg-slate-800">
                  <Play className="w-4 h-4 text-white/50" />
                </div>
              )}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-6 h-6 rounded-full bg-black/50 flex items-center justify-center">
                  <Play className="w-3 h-3 text-white ml-0.5" />
                </div>
              </div>
              <div className="absolute bottom-0.5 right-0.5 bg-black/70 text-white text-[7px] px-0.5 rounded">
                {Math.floor((selectedVideo.duration || 0) / 60)}:{String(Math.floor((selectedVideo.duration || 0) % 60)).padStart(2, '0')}
              </div>
            </div>
            <button
              onClick={() => setSelectedVideo(null)}
              className="absolute -top-1.5 -right-1.5 bg-slate-700 hover:bg-slate-900 text-white rounded-full p-0.5 shadow-sm transition-colors"
              data-testid="remove-dm-video-btn"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>

      <GifPickerDialog
        open={showGifPicker}
        onOpenChange={setShowGifPicker}
        onSelect={handleGifSelect}
      />

      {onSendPoll && (
        <PollCreatorDialog
          open={showPollCreator}
          onOpenChange={setShowPollCreator}
          onCreate={onSendPoll}
        />
      )}
    </div>
  );
}
